(() => {
  const WINDOW_RADIUS = 2;
  const PRELOAD_AHEAD = 3;
  const PRELOAD_CONCURRENCY = 2;
  const VIEW_THRESHOLD_MS = 800;
  const VARIANT_RAIL_MAX = 7;
  const SPINNER_MIN_MS = 350;
  const BATCH_SIZE = 12;
  const RECENT_MEMORY = 8;
  const NEW_PRODUCT_DAYS = 60;
  const SEEN_LIMIT = 500;
  const SEEN_KEY = 'sf-seen';
  const AFFINITY_KEY = 'sf-affinity';
  const SOUND_KEY = 'sf-sound';
  const VIDEOS_SEEN_KEY = 'lp-videos-vistos';
  const VIDEOS_SEEN_LIMIT = 200;
  const MAX_PLAYERS = 3;
  const QUALIFIED_MS = 3000;
  const PROGRESS_MARKS = [25, 50, 75, 100];
  const POOL_SECTION = 'swipe-feed-pool';
  const IMAGE_WIDTH = Math.min(1000, Math.ceil((480 * (window.devicePixelRatio || 1)) / 200) * 200);

  const ICONS = {
    close:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>',
    cart: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M6 7h12l-1 12H7L6 7Z"/><path d="M9 7a3 3 0 0 1 6 0"/></svg>',
    info: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 11v5M12 8h.01"/></svg>',
    swipe:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14"/><path d="m6 13 6 6 6-6"/></svg>',
    star: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="m12 3 2.6 5.6 6 .8-4.4 4.2 1.1 6-5.3-2.9L6.7 19.6l1.1-6L3.4 9.4l6-.8L12 3Z"/></svg>',
    muted:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 9h4l5-4v14l-5-4H4V9Z"/><path d="m17 9 4 6M21 9l-4 6"/></svg>',
    sound:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 9h4l5-4v14l-5-4H4V9Z"/><path d="M16.5 8.5a5 5 0 0 1 0 7M19 6a8.5 8.5 0 0 1 0 12"/></svg>',
  };

  const srcAt = (url, width) => {
    if (!url) return '';
    return /[?&]width=\d+/.test(url)
      ? url.replace(/([?&])width=\d+/, `$1width=${width}`)
      : `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  };

  const parseJson = (text, fallback) => {
    try {
      return JSON.parse(text) ?? fallback;
    } catch (error) {
      return fallback;
    }
  };

  const readStorage = (key, fallback) => {
    try {
      return parseJson(sessionStorage.getItem(key), fallback);
    } catch (error) {
      return fallback;
    }
  };

  const writeStorage = (key, value) => {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      return;
    }
  };

  const rememberVideo = (id) => {
    try {
      const seen = parseJson(localStorage.getItem(VIDEOS_SEEN_KEY), []).filter((entry) => entry !== String(id));
      seen.push(String(id));
      localStorage.setItem(VIDEOS_SEEN_KEY, JSON.stringify(seen.slice(-VIDEOS_SEEN_LIMIT)));
    } catch (error) {
      return;
    }
  };

  const weightedOrder = (items, weightOf) =>
    items
      .map((item) => ({ item, key: Math.pow(Math.random(), 1 / Math.max(weightOf(item), 0.001)) }))
      .sort((a, b) => b.key - a.key)
      .map((entry) => entry.item);

  // Pool items are products; video slides carry `sv` (the video), `ps` (products sharing it) and `k` (slide key).
  const keyOf = (item) => item.k || item.id;
  const sameId = (a, b) => b != null && String(a) === String(b);
  const firstAvailable = (product) => Math.max(0, product.v.findIndex((variant) => variant.av));
  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const mutedVideo = () => {
    const video = document.createElement('video');
    video.className = 'sf-video';
    video.muted = true;
    video.preload = 'none';
    ['muted', 'playsinline', 'loop', 'disablepictureinpicture'].forEach((name) => video.setAttribute(name, ''));
    return video;
  };

  class PreloadQueue {
    constructor(concurrency) {
      this.concurrency = concurrency;
      this.pending = [];
      this.running = 0;
      this.done = new Set();
    }

    add(url) {
      if (!url || this.done.has(url)) return;
      this.done.add(url);
      this.pending.push(url);
      this.drain();
    }

    clear() {
      this.pending.length = 0;
    }

    drain() {
      while (this.running < this.concurrency && this.pending.length) {
        const url = this.pending.shift();
        this.running += 1;
        const image = new Image();
        const release = () => {
          this.running -= 1;
          this.drain();
        };
        image.onload = release;
        image.onerror = release;
        image.src = url;
      }
    }
  }

  class SwipeFeed extends HTMLElement {
    connectedCallback() {
      this.config = parseJson(this.querySelector('[data-swipe-feed-config]')?.textContent, null);
      if (!this.config) return;

      this.mode = this.dataset.mode || 'productos';
      this.labels = {
        add: this.dataset.addLabel,
        added: this.dataset.addedLabel,
        soldOut: this.dataset.soldOutLabel,
        error: this.dataset.errorLabel,
        reviews: this.dataset.reviewsLabel,
        close: this.dataset.closeLabel,
        product: this.dataset.productLabel,
        soundOn: this.dataset.soundOnLabel,
        soundOff: this.dataset.soundOffLabel,
      };
      this.swatches = parseJson(this.querySelector('[data-swipe-feed-swatches]')?.textContent, {});
      this.analyticsEnabled = this.dataset.analytics !== 'false';
      this.heartbeatMs = Math.max(5, parseInt(this.dataset.heartbeat, 10) || 15) * 1000;
      this.seenIds = new Set(readStorage(SEEN_KEY, []));
      this.affinity = readStorage(AFFINITY_KEY, {});

      this.querySelector('[data-sf-open]')?.addEventListener('click', () => this.open());
      this.initReel();
      if (this.dataset.designMode === 'true') this.renderSegmentStats();
      this.openFromLink();
    }

    openFromLink() {
      const value = new URLSearchParams(window.location.search).get('swipe');
      if (value === null || this.dataset.launcher === 'true') return;

      const targeted = value === this.dataset.sectionId;
      const generic = ['', '1', 'true'].includes(value) && document.querySelector('swipe-feed:not([data-launcher])') === this;
      if (!targeted && !generic) return;

      this.openSource = 'shared_link';
      requestAnimationFrame(() => this.open({ surface: 'enlace' }));
    }

    initReel() {
      const reel = this.querySelector('[data-sf-reel]');
      const tiles = Array.from(reel?.querySelectorAll('[data-sf-video]') || []);
      if (!tiles.length) return;

      tiles.forEach((tile) =>
        tile.addEventListener('click', () =>
          this.open({ surface: 'inicio', startProductId: tile.dataset.sfProduct, startVideoId: tile.dataset.sfVideo })
        )
      );
      const warm = () => this.loadPool().catch(() => {});
      ['pointerenter', 'touchstart'].forEach((type) => reel.addEventListener(type, warm, { once: true, passive: true }));

      let timer;
      const impression = new IntersectionObserver(
        ([entry]) => {
          clearTimeout(timer);
          if (!entry.isIntersecting) return;
          timer = setTimeout(() => {
            impression.disconnect();
            this.track('video_impression', { surface: 'inicio', video_ids: tiles.map((tile) => tile.dataset.sfVideo) });
          }, 1000);
        },
        { threshold: 0.5 }
      );
      impression.observe(reel);

      if (prefersReducedMotion() || navigator.connection?.saveData) return;
      const idle = () => ('requestIdleCallback' in window ? requestIdleCallback(() => this.startPreview(tiles)) : setTimeout(() => this.startPreview(tiles), 200));
      if (document.readyState === 'complete') idle();
      else window.addEventListener('load', idle, { once: true });
    }

    // Only the first visible tile plays a muted loop (HLS where native, else the smallest mp4).
    startPreview(tiles) {
      const video = mutedVideo();
      const hls = video.canPlayType('application/vnd.apple.mpegurl');
      const sourceOf = (tile) => (hls && tile.dataset.previewHls) || tile.dataset.preview;
      let host = null;
      this.preview = video;
      video.addEventListener('playing', () => video.classList.add('is-playing'), { once: true });

      const watcher = new IntersectionObserver(
        (entries) => {
          entries.forEach(({ target, isIntersecting }) => {
            if (!host && isIntersecting && sourceOf(target)) {
              host = target;
              video.src = sourceOf(target);
              target.appendChild(video);
              tiles.forEach((tile) => tile !== target && watcher.unobserve(tile));
            }
            if (target !== host) return;
            if (isIntersecting && !this.overlay) video.play().catch(() => {});
            else video.pause();
          });
        },
        { threshold: 0.6 }
      );
      tiles.forEach((tile) => watcher.observe(tile));
    }

    loadPool() {
      if (!this.poolPromise) {
        this.poolPromise = this.fetchPool()
          .then((products) => this.entriesFor(products))
          .catch((error) => {
            this.poolPromise = null;
            throw error;
          });
      }
      return this.poolPromise;
    }

    async fetchPool() {
      const handles = [...new Set(this.config.segments.map((segment) => segment.collection))];
      const fetched = await Promise.all(handles.map((handle) => this.fetchCollection(handle)));
      const byHandle = Object.fromEntries(handles.map((handle, position) => [handle, fetched[position]]));

      const seen = new Set();
      const pool = [];
      this.segmentStats = [];
      this.segmentSizes = [];

      this.config.segments.forEach((segment, segmentIndex) => {
        const stats = { name: segment.name, found: 0, used: 0, duplicates: 0 };
        for (const product of byHandle[segment.collection] || []) {
          if (this.mode === 'videos' && !product.vd?.length) continue;
          if (!this.passesGlobalFilters(product) || !this.matchesSegment(product, segment)) continue;
          stats.found += 1;
          if (seen.has(product.id)) {
            stats.duplicates += 1;
            continue;
          }
          if (stats.used >= segment.max || pool.length >= this.config.maxProducts) continue;
          seen.add(product.id);
          pool.push({ ...product, i: product.i.slice(0, this.config.maxImages), segment: segmentIndex });
          stats.used += 1;
        }
        this.segmentStats.push(stats);
        this.segmentSizes.push(stats.used);
      });

      return pool;
    }

    async fetchCollection(handle) {
      const url = `${Shopify.routes.root}collections/${encodeURIComponent(handle)}?section_id=${POOL_SECTION}`;
      const response = await fetch(url);
      if (!response.ok) return [];
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      return parseJson(doc.querySelector('[data-swipe-feed-pool]')?.textContent, []);
    }

    // videos: one slide per video id, products sharing a video become chips. mixto: video first, then images.
    entriesFor(products) {
      if (this.mode === 'mixto') return products.map((product) => (product.vd?.length ? { ...product, sv: product.vd[0] } : product));
      if (this.mode !== 'videos') return products;

      const byVideo = new Map();
      products.forEach((product) =>
        (product.vd || []).forEach((video) => {
          const entry = byVideo.get(video.id);
          if (entry) entry.ps.push(product);
          else byVideo.set(video.id, { ...product, sv: video, ps: [product], k: `v${video.id}` });
        })
      );
      return [...byVideo.values()];
    }

    passesGlobalFilters(product) {
      if (this.config.hideSoldOut && !product.av) return false;
      if (this.config.blacklistHandles.includes(product.h)) return false;
      if (this.config.blacklistSkus.length) {
        const skus = product.sk.split(',');
        if (this.config.blacklistSkus.some((sku) => skus.includes(sku))) return false;
      }
      return true;
    }

    matchesSegment(product, segment) {
      if (segment.types.length && !segment.types.includes(product.ty)) return false;
      if (segment.vendors.length && !segment.vendors.includes(product.ve)) return false;
      if (segment.tags.length) {
        const tags = product.tg.split(',');
        if (!segment.tags.some((tag) => tags.includes(tag))) return false;
      }
      return true;
    }

    async renderSegmentStats() {
      const list = this.closest('.sf-section')?.querySelector('[data-sf-segment-stats]');
      if (!list) return;

      try {
        const pool = await this.loadPool();
        const rows = this.segmentStats.map((stats) => {
          const row = document.createElement('li');
          row.textContent = `${stats.name}: ${stats.found} coinciden · ${stats.used} en el feed · ${stats.duplicates} ya incluidos en otro segmento`;
          if (!stats.used) row.classList.add('is-empty');
          return row;
        });
        const total = document.createElement('li');
        total.className = 'is-total';
        total.textContent = `Total en el feed: ${pool.length} de ${this.config.maxProducts} posibles`;
        list.replaceChildren(...rows, total);
      } catch (error) {
        list.replaceChildren();
        list.append(Object.assign(document.createElement('li'), { textContent: 'No se pudo cargar la selección.' }));
      }
    }

    weightOf(item) {
      const strength = this.config.weightStrength / 100;
      const ageDays = (Date.now() / 1000 - item.c) / 86400;
      const signal =
        Math.log1p(item.s) * 2 +
        (item.r ? (item.r * Math.log1p(item.rc)) / 2 : 0) +
        (ageDays <= NEW_PRODUCT_DAYS ? 3 : 0) +
        (item.v.some((variant) => variant.cp) ? 2 : 0);

      const segment = this.config.segments[item.segment];
      let segmentFactor = segment?.weight || 1;
      if (this.config.segmentMode === 'equal') segmentFactor /= this.segmentSizes[item.segment] || 1;

      const affinity = (this.affinity[item.ty] || 0) + (this.affinity[item.ve] || 0);
      const startType = this.startType && item.ty === this.startType ? 2 : 1;
      return (1 + strength * signal) * segmentFactor * (1 + 0.5 * affinity) * startType;
    }

    initialOrder() {
      const weightOf = (item) => this.weightOf(item);
      const unseen = this.pool.filter((item) => !this.seenIds.has(keyOf(item)));
      const seen = this.pool.filter((item) => this.seenIds.has(keyOf(item)));
      return [...weightedOrder(unseen, weightOf), ...weightedOrder(seen, weightOf)];
    }

    nextBatch() {
      const recent = new Set(this.items.slice(-RECENT_MEMORY).map(keyOf));
      const candidates = this.pool.length > RECENT_MEMORY ? this.pool.filter((item) => !recent.has(keyOf(item))) : this.pool;
      return weightedOrder(candidates, (item) => this.weightOf(item)).slice(0, BATCH_SIZE);
    }

    findStart(items) {
      const { productId, videoId } = this.start;
      return (
        items.find((item) => sameId(item.sv?.id, videoId)) ||
        items.find((item) => sameId(item.id, productId) || item.ps?.some((product) => sameId(product.id, productId))) ||
        null
      );
    }

    // options: { surface, startProductId, startVideoId, startItem } (see _workbench/videos/INTERFACE.md)
    async open(options = {}) {
      if (this.overlay) return;

      this.surface = options.surface || this.dataset.surface || 'inicio';
      this.start = { productId: options.startProductId, videoId: options.startVideoId };
      this.startType = options.startItem?.ty;
      this.returnFocus = document.activeElement;
      this.sessionId = crypto.randomUUID ? crypto.randomUUID() : String(Date.now());
      this.stats = { swipes: 0, viewed: 0, adds: 0, startedAt: Date.now() };
      this.activeIndex = -1;
      this.items = [];
      this.pool = null;
      this.slideState = new Map();
      this.filledIndices = new Set();
      this.preloader = new PreloadQueue(PRELOAD_CONCURRENCY);
      this.players = [];
      this.videoStats = new Map();
      this.videoView = null;
      this.soundOn = readStorage(SOUND_KEY, false) === true;
      this.reducedMotion = prefersReducedMotion();
      this.canHls = Boolean(document.createElement('video').canPlayType('application/vnd.apple.mpegurl'));
      const connection = navigator.connection;
      this.slowNetwork = Boolean(connection?.saveData || /2g|3g/.test(connection?.effectiveType || ''));
      this.preview?.pause();

      this.buildOverlay();
      const overlay = this.overlay;
      history.pushState({ sfOverlay: true }, '');
      this.historyPushed = true;
      document.documentElement.classList.add('sf-locked');
      requestAnimationFrame(() => overlay.classList.add('is-open'));
      overlay.querySelector('[data-sf-close]').focus();

      if (options.startItem) {
        const entries = this.entriesFor([options.startItem]);
        const first = this.findStart(entries) || entries[0];
        if (first) this.showItems([first]);
      }

      try {
        this.pool = await this.loadPool();
      } catch (error) {
        this.pool = [];
      }
      if (this.overlay !== overlay) return;

      this.startType ||= this.pool.find((item) => sameId(item.id, this.start.productId))?.ty;
      const ordered = this.initialOrder();

      if (this.items.length) {
        const first = this.items[0];
        const match = ordered.find((item) => keyOf(item) === keyOf(first));
        if (match?.ps?.length > first.ps?.length) {
          first.ps = [first.ps[0], ...match.ps.filter((product) => product.id !== first.ps[0].id)];
          if (this.filledIndices.has(0)) this.renderCard(this.slides[0], 0);
        }
        this.appendSlides(ordered.filter((item) => keyOf(item) !== keyOf(first)));
      } else if (ordered.length) {
        const first = this.findStart(ordered);
        this.showItems(first ? [first, ...ordered.filter((item) => item !== first)] : ordered);
      } else {
        this.loadingEl.remove();
        this.trackEl.append(Object.assign(document.createElement('p'), { className: 'sf-empty', textContent: this.labels.error }));
        return;
      }

      this.track('swipe_feed_opened', {
        pool_size: this.pool.length,
        source: this.openSource || 'teaser',
      });
    }

    showItems(items) {
      this.loadingEl.remove();
      this.items = items;
      this.appendSlides(items, true);

      const pick = items[0].ps?.findIndex((product) => sameId(product.id, this.start.productId));
      if (pick > 0) Object.assign(this.stateAt(0), { product: pick, variant: firstAvailable(items[0].ps[pick]) });

      this.setActive(0);
      if (items[0].sv) this.track('video_open', this.videoProps(0));
      this.showHintOnce();
      this.syncCartCount();
      this.startHeartbeat();
    }

    close(reason = 'close') {
      if (!this.overlay) return;

      this.commitView();
      this.commitVideoView(reason === 'pagehide' ? 'hidden' : 'close');
      this.track('swipe_feed_closed', {
        swipes: this.stats.swipes,
        products_viewed: this.stats.viewed,
        adds: this.stats.adds,
        duration_ms: Date.now() - this.stats.startedAt,
      });

      writeStorage(SEEN_KEY, [...this.seenIds].slice(-SEEN_LIMIT));
      writeStorage(AFFINITY_KEY, this.affinity);

      clearInterval(this.heartbeatTimer);
      document.removeEventListener('keydown', this.onKeyDown);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      window.removeEventListener('pagehide', this.onPageHide);
      window.removeEventListener('popstate', this.onPopState);
      this.observer?.disconnect();
      this.preloader.clear();
      this.players.forEach((player) => this.releasePlayer(player));

      // UI close removes our history entry; back button (popstate) and page unload already did.
      if (this.historyPushed && reason === 'close' && history.state?.sfOverlay) history.back();
      this.historyPushed = false;

      const overlay = this.overlay;
      this.overlay = null;
      this.openSource = null;
      overlay.classList.remove('is-open');
      document.documentElement.classList.remove('sf-locked');
      setTimeout(() => overlay.remove(), 200);
      this.returnFocus?.focus?.();
    }

    buildOverlay() {
      const overlay = document.createElement('div');
      overlay.className = 'sf-overlay';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.setAttribute('aria-label', this.mode === 'productos' ? 'Feed' : 'Videos');
      overlay.tabIndex = -1;
      overlay.innerHTML = `
        <div class="sf-frame">
          <div class="sf-track" data-sf-track>
            <div class="sf-loading" data-sf-loading></div>
          </div>
          <header class="sf-topbar">
            <button type="button" class="sf-icon-button" data-sf-close>${ICONS.close}</button>
            <div class="sf-topbar__end">
              <button type="button" class="sf-icon-button" data-sf-sound hidden></button>
              <button type="button" class="sf-icon-button sf-cart" data-sf-cart aria-label="Carrito">
                ${ICONS.cart}<span class="sf-cart__count" data-sf-count hidden>0</span>
              </button>
            </div>
          </header>
        </div>`;

      const accent = getComputedStyle(this).getPropertyValue('--sf-accent').trim();
      if (accent) overlay.style.setProperty('--sf-accent', accent);
      overlay.querySelector('[data-sf-close]').setAttribute('aria-label', this.labels.close);
      document.body.appendChild(overlay);
      this.overlay = overlay;
      this.trackEl = overlay.querySelector('[data-sf-track]');
      this.loadingEl = overlay.querySelector('[data-sf-loading]');
      this.countEl = overlay.querySelector('[data-sf-count]');
      this.cartButton = overlay.querySelector('[data-sf-cart]');
      this.soundButton = overlay.querySelector('[data-sf-sound]');

      overlay.querySelector('[data-sf-close]').addEventListener('click', () => this.close());
      this.cartButton.addEventListener('click', () => this.openCartDrawer());
      this.soundButton.addEventListener('click', () => this.toggleSound());

      this.onKeyDown = (event) => this.handleKey(event);
      this.onVisibilityChange = () => this.handleVisibility();
      this.onPageHide = () => this.close('pagehide');
      this.onPopState = () => this.close('popstate');
      document.addEventListener('keydown', this.onKeyDown);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      window.addEventListener('pagehide', this.onPageHide);
      window.addEventListener('popstate', this.onPopState);

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.setActive(Number(entry.target.dataset.index));
          });
        },
        { root: this.trackEl, threshold: 0.6 }
      );
    }

    appendSlides(items, initial = false) {
      const offset = this.trackEl.querySelectorAll('.sf-slide').length;
      const fragment = document.createDocumentFragment();

      items.forEach((item, position) => {
        const slide = document.createElement('article');
        slide.className = 'sf-slide';
        slide.dataset.index = String(offset + position);
        fragment.appendChild(slide);
      });

      this.trackEl.appendChild(fragment);
      this.slides = Array.from(this.trackEl.querySelectorAll('.sf-slide'));
      this.slides.slice(offset).forEach((slide) => this.observer.observe(slide));
      if (!initial) this.items = this.items.concat(items);
    }

    itemAt(index) {
      return this.items[index];
    }

    // Product shown in the card; differs from the item when a shared video's chip is selected.
    productAt(index) {
      const item = this.itemAt(index);
      return item?.ps?.[this.stateAt(index).product] || item;
    }

    stateAt(index) {
      if (!this.slideState.has(index)) {
        this.slideState.set(index, { image: 0, product: 0, variant: firstAvailable(this.itemAt(index)) });
      }
      return this.slideState.get(index);
    }

    // Media list per slide: poster of the video first (not in videos mode: followed by the product images).
    mediaOf(item) {
      if (!item.sv) return item.i;
      return this.mode === 'videos' ? [item.sv.p] : [item.sv.p, ...item.i];
    }

    setActive(index) {
      if (index === this.activeIndex) return;

      this.commitView();
      this.commitVideoView('swipe');
      if (this.activeIndex >= 0) this.stats.swipes += 1;

      this.activeIndex = index;
      this.viewStartedAt = Date.now();
      this.updateWindow();
      this.slides[index]?.classList.toggle('is-paused', this.reducedMotion);
      this.syncPlayers();
      this.preloadAhead();
      this.extendIfNeeded();
    }

    updateWindow() {
      for (const index of this.filledIndices) {
        if (Math.abs(index - this.activeIndex) > WINDOW_RADIUS) {
          this.slides[index].replaceChildren();
          this.filledIndices.delete(index);
        }
      }
      for (let index = this.activeIndex - WINDOW_RADIUS; index <= this.activeIndex + WINDOW_RADIUS; index++) {
        if (index >= 0 && index < this.slides.length && !this.filledIndices.has(index)) this.fillSlide(this.slides[index], index);
      }
    }

    fillSlide(slide, index) {
      const item = this.itemAt(index);
      if (!item) return;

      const video = item.sv
        ? `<div class="sf-progress"><span></span></div>
           <button type="button" class="sf-tap sf-tap--play${this.mode === 'videos' ? ' sf-tap--full' : ''}" aria-label="Pausar o reproducir"></button>`
        : '';
      const steps =
        this.mode === 'videos'
          ? ''
          : `<button type="button" class="sf-tap sf-tap--prev" aria-label="Anterior"></button>
             <button type="button" class="sf-tap sf-tap--next" aria-label="Siguiente"></button>`;

      this.filledIndices.add(index);
      slide.innerHTML = `
        <div class="sf-slide__blur"></div>
        <div class="sf-slide__media">
          <img class="sf-slide__img" alt="" decoding="async">
        </div>
        ${steps}
        ${video}
        <div class="sf-dots"></div>
        <div class="sf-variants"></div>
        <div class="sf-slide__info">
          <div class="sf-chips"></div>
          <div class="sf-slide__head">
            <a class="sf-info-link" target="_blank" rel="noopener">${ICONS.info}</a>
            <div>
              <h3 class="sf-slide__title"></h3>
              <div class="sf-rating"></div>
            </div>
          </div>
          <div class="sf-price"></div>
          <button type="button" class="sf-add"></button>
        </div>`;

      const infoLink = slide.querySelector('.sf-info-link');
      infoLink.setAttribute('aria-label', this.labels.product);

      slide.querySelector('.sf-tap--prev')?.addEventListener('click', () => this.stepImage(index, -1));
      slide.querySelector('.sf-tap--next')?.addEventListener('click', () => this.stepImage(index, 1));
      slide.querySelector('.sf-tap--play')?.addEventListener('click', () => this.togglePlay(index));
      infoLink.addEventListener('click', () => {
        const product = this.productAt(index);
        this.track('swipe_feed_product_opened', { product_id: product.id, handle: product.h, position: index });
        if (item.sv) this.track('video_product_click', this.videoProps(index));
      });
      slide.querySelector('.sf-add').addEventListener('click', (event) => this.addToCart(index, event.currentTarget));

      this.renderCard(slide, index);
      this.renderImage(slide, index);
    }

    renderCard(slide, index) {
      const product = this.productAt(index);
      slide.querySelector('.sf-slide__title').textContent = product.t;
      slide.querySelector('.sf-info-link').href = product.u;
      this.renderChips(slide, index);
      this.renderRating(slide, product);
      this.renderVariants(slide, index);
    }

    renderChips(slide, index) {
      const item = this.itemAt(index);
      const rail = slide.querySelector('.sf-chips');
      if (!item.ps || item.ps.length < 2) {
        rail.replaceChildren();
        return;
      }

      const selected = this.stateAt(index).product;
      rail.replaceChildren(
        ...item.ps.map((product, position) => {
          const chip = document.createElement('button');
          chip.type = 'button';
          chip.className = 'sf-chip';
          chip.textContent = product.t;
          chip.setAttribute('aria-pressed', String(position === selected));
          chip.addEventListener('click', () => {
            Object.assign(this.stateAt(index), { product: position, variant: firstAvailable(product) });
            this.renderCard(slide, index);
          });
          return chip;
        })
      );
    }

    renderRating(slide, item) {
      const container = slide.querySelector('.sf-rating');
      if (!item.r || !this.config.showRating) {
        container.replaceChildren();
        return;
      }

      const filled = Math.round(item.r);
      const stars = document.createElement('span');
      stars.className = 'sf-stars';
      stars.innerHTML = Array.from({ length: 5 }, (_, position) =>
        position < filled ? ICONS.star : ICONS.star.replace('<svg', '<svg class="sf-star--empty"')
      ).join('');

      const value = document.createElement('span');
      value.textContent = item.r;
      container.replaceChildren(stars, value);

      if (item.rc) {
        const count = document.createElement('span');
        count.textContent = `(${item.rc} ${this.labels.reviews})`;
        container.appendChild(count);
      }
    }

    swatchFor(title) {
      const key = String(title).split('/')[0].trim().toLowerCase();
      return this.swatches[key] || null;
    }

    shortLabel(title) {
      const label = String(title).split('/')[0].trim();
      return label.length > 3 ? label.slice(0, 2).toUpperCase() : label.toUpperCase();
    }

    renderVariants(slide, index) {
      const item = this.productAt(index);
      const rail = slide.querySelector('.sf-variants');
      if (!rail) return;

      if (item.v.length < 2) {
        rail.replaceChildren();
        this.renderPrice(slide, index);
        return;
      }

      const selected = this.stateAt(index).variant;
      const shown = item.v.slice(0, VARIANT_RAIL_MAX);

      const dots = shown.map((variant, position) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'sf-variant';
        dot.disabled = !variant.av;
        dot.title = variant.t;
        dot.setAttribute('aria-label', variant.t);
        dot.setAttribute('aria-pressed', String(position === selected));

        const color = this.swatchFor(variant.t);
        if (color) {
          dot.style.setProperty('--sf-swatch', color);
        } else {
          dot.classList.add('sf-variant--text');
          dot.textContent = this.shortLabel(variant.t);
        }

        dot.addEventListener('click', () => {
          this.stateAt(index).variant = position;
          this.renderVariants(slide, index);
        });
        return dot;
      });

      if (item.v.length > shown.length) {
        const more = document.createElement('a');
        more.className = 'sf-variant sf-variant--more';
        more.href = item.u;
        more.target = '_blank';
        more.rel = 'noopener';
        more.textContent = `+${item.v.length - shown.length}`;
        more.setAttribute('aria-label', this.labels.product);
        dots.push(more);
      }

      rail.replaceChildren(...dots);
      this.renderPrice(slide, index);
    }

    renderPrice(slide, index) {
      const price = slide?.querySelector('.sf-price');
      const button = slide?.querySelector('.sf-add');
      const variant = this.productAt(index)?.v[this.stateAt(index).variant];
      if (!price || !button || !variant) return;

      const now = document.createElement('span');
      now.textContent = variant.p;
      price.replaceChildren(now);

      if (variant.cp) {
        const was = document.createElement('s');
        was.className = 'sf-price__was';
        was.textContent = variant.cp;
        price.appendChild(was);
      }

      button.disabled = !variant.av;
      button.textContent = variant.av ? this.labels.add : this.labels.soldOut;
    }

    renderImage(slide, index) {
      const item = this.itemAt(index);
      const state = this.stateAt(index);
      const media = this.mediaOf(item);
      const url = media[state.image];
      const image = slide.querySelector('.sf-slide__img');
      const blur = slide.querySelector('.sf-slide__blur');
      const isActive = index === this.activeIndex;
      const onVideo = Boolean(item.sv) && state.image === 0;

      blur.style.backgroundImage = `url("${srcAt(url, 40)}")`;
      slide.classList.toggle('sf-slide--video', onVideo);
      slide.classList.toggle('sf-slide--cover', onVideo && item.sv.ar < 0.8);

      image.classList.remove('is-ready');
      image.fetchPriority = isActive ? 'high' : 'low';
      image.loading = 'eager';
      image.src = srcAt(url, IMAGE_WIDTH);
      image.alt = onVideo ? item.sv.a : item.t;

      const reveal = () => image.classList.add('is-ready');
      if (image.decode) {
        image.decode().then(reveal).catch(reveal);
      } else {
        image.addEventListener('load', reveal, { once: true });
      }

      this.renderDots(slide, index);
      if (isActive && !onVideo) media.slice(1).forEach((next) => this.preloader.add(srcAt(next, IMAGE_WIDTH)));
    }

    renderDots(slide, index) {
      const media = this.mediaOf(this.itemAt(index));
      const dots = slide.querySelector('.sf-dots');
      if (media.length < 2) {
        dots.replaceChildren();
        return;
      }

      const current = this.stateAt(index).image;
      dots.replaceChildren(
        ...media.map((_, position) => {
          const dot = document.createElement('span');
          dot.className = position === current ? 'sf-dot is-active' : 'sf-dot';
          return dot;
        })
      );
    }

    stepImage(index, direction) {
      const item = this.itemAt(index);
      if (!item || !this.filledIndices.has(index)) return;
      const count = this.mediaOf(item).length;
      const state = this.stateAt(index);
      const next = state.image + direction;

      if (next < 0 || count < 2) return;
      state.image = next >= count ? 0 : next;

      this.renderImage(this.slides[index], index);
      if (item.sv) this.syncPlayers();
      this.track('swipe_feed_image_viewed', {
        product_id: item.id,
        handle: item.h,
        image_index: state.image,
        position: index,
      });
    }

    preloadAhead() {
      for (let offset = 1; offset <= PRELOAD_AHEAD; offset++) {
        const item = this.itemAt(this.activeIndex + offset);
        if (item) this.preloader.add(srcAt(this.mediaOf(item)[0], IMAGE_WIDTH));
      }
    }

    extendIfNeeded() {
      if (!this.pool?.length || this.items.length - this.activeIndex > 5) return;
      this.appendSlides(this.nextBatch());
    }

    // Video playback: at most MAX_PLAYERS recycled <video> elements for the active, next and previous slide.
    showsVideo(index) {
      return Boolean(this.itemAt(index)?.sv) && this.filledIndices.has(index) && this.stateAt(index).image === 0;
    }

    playerAt(index) {
      return this.players.find((player) => player.index === index);
    }

    syncPlayers() {
      const wanted = [this.activeIndex, this.activeIndex + 1, this.activeIndex - 1].filter((index) => this.showsVideo(index));
      this.players.forEach((player) => {
        if (player.index >= 0 && !wanted.includes(player.index)) this.releasePlayer(player);
      });
      wanted.forEach((index) => {
        if (!this.playerAt(index)) this.attachPlayer(this.players.find((player) => player.index < 0) || this.createPlayer(), index);
      });

      const active = this.playerAt(this.activeIndex);
      this.players.forEach((player) => player !== active && player.el.pause());
      this.renderSoundButton();
      if (!active) {
        this.commitVideoView('swipe');
        this.loadNeighbors();
        return;
      }
      if (this.videoView?.index !== this.activeIndex) {
        this.videoView = { index: this.activeIndex, id: active.video.id, last: active.el.currentTime, watchMs: 0, percent: 0 };
      }
      this.playActive(active);
    }

    createPlayer() {
      const player = { el: mutedVideo(), index: -1, video: null };
      player.el.addEventListener('playing', () => this.handlePlaying(player));
      player.el.addEventListener('timeupdate', () => this.handleTimeUpdate(player));
      this.players.push(player);
      return player;
    }

    attachPlayer(player, index) {
      player.index = index;
      player.video = this.itemAt(index).sv;
      player.el.muted = !this.soundOn;
      player.el.classList.remove('is-playing');
      this.slides[index].querySelector('.sf-slide__media').appendChild(player.el);
    }

    releasePlayer(player) {
      const { el } = player;
      el.pause();
      el.removeAttribute('src');
      el.load();
      el.remove();
      el.classList.remove('is-playing');
      player.index = -1;
      player.video = null;
    }

    sourceFor(video) {
      if (video.hls && this.canHls) return video.hls;
      return (this.slowNetwork ? video.s4 : video.s7) || video.s4 || video.hls;
    }

    loadPlayer(player, preload) {
      player.el.preload = preload;
      if (!player.el.getAttribute('src')) player.el.src = this.sourceFor(player.video);
    }

    // Neighbours only start downloading once the active slide plays (or shows no video).
    loadNeighbors() {
      this.players.forEach((player) => {
        if (player.index >= 0 && player.index !== this.activeIndex) {
          this.loadPlayer(player, player.index === this.activeIndex + 1 ? 'auto' : 'metadata');
        }
      });
    }

    playActive(player) {
      this.loadPlayer(player, 'auto');
      if (document.hidden || this.slides[player.index].classList.contains('is-paused')) return;

      player.el.play().catch((error) => {
        if (error.name !== 'NotAllowedError' || player.el.muted) return;
        // Sound without a fresh gesture was refused (iOS): continue muted.
        this.soundOn = false;
        this.players.forEach(({ el }) => (el.muted = true));
        this.renderSoundButton();
        if (player.index === this.activeIndex) player.el.play().catch(() => {});
      });
    }

    togglePlay(index) {
      const player = this.playerAt(index);
      if (!player) {
        this.stepImage(index, 1);
        return;
      }

      const pause = !player.el.paused;
      this.slides[index].classList.toggle('is-paused', pause);
      if (pause) player.el.pause();
      else this.playActive(player);
    }

    toggleSound() {
      this.soundOn = !this.soundOn;
      writeStorage(SOUND_KEY, this.soundOn);
      while (this.players.length < MAX_PLAYERS) this.createPlayer();

      this.players.forEach(({ el, index }) => {
        el.muted = !this.soundOn;
        if (!this.soundOn || index === this.activeIndex) return;
        // iOS unlocks sound per element and only inside a user gesture.
        el.play().catch(() => {});
        el.pause();
      });
      this.renderSoundButton();
    }

    renderSoundButton() {
      if (!this.soundButton) return;
      this.soundButton.hidden = !this.playerAt(this.activeIndex);
      this.soundButton.innerHTML = this.soundOn ? ICONS.sound : ICONS.muted;
      this.soundButton.setAttribute('aria-label', this.soundOn ? this.labels.soundOff : this.labels.soundOn);
    }

    videoProps(index) {
      return { video_id: this.itemAt(index)?.sv?.id, product_id: this.productAt(index)?.id, position: index };
    }

    videoStatsFor(id) {
      if (!this.videoStats.has(id)) this.videoStats.set(id, { marks: new Set(), watchMs: 0, played: false, qualified: false });
      return this.videoStats.get(id);
    }

    handlePlaying(player) {
      if (player.index !== this.activeIndex || !this.overlay) return;
      player.el.classList.add('is-playing');

      const stats = this.videoStatsFor(player.video.id);
      if (!stats.played) {
        stats.played = true;
        this.track('video_play', { ...this.videoProps(player.index), ttff_ms: Date.now() - this.viewStartedAt });
      }
      this.loadNeighbors();
    }

    handleTimeUpdate(player) {
      const view = this.videoView;
      if (player.index !== this.activeIndex || !view) return;

      const { el } = player;
      const duration = Number.isFinite(el.duration) && el.duration > 0 ? el.duration : player.video.d / 1000;
      if (!duration) return;

      const delta = el.currentTime - view.last;
      const percent = delta < -0.5 ? 100 : Math.min(100, (el.currentTime / duration) * 100);
      const stats = this.videoStatsFor(view.id);
      view.last = el.currentTime;
      view.percent = Math.max(view.percent, percent);
      this.slides[player.index].querySelector('.sf-progress span')?.style.setProperty('transform', `scaleX(${el.currentTime / duration})`);

      if (delta > 0 && delta < 1.5) {
        view.watchMs += delta * 1000;
        stats.watchMs += delta * 1000;
        if (!stats.qualified && stats.watchMs >= QUALIFIED_MS) {
          stats.qualified = true;
          rememberVideo(view.id);
          this.track('video_qualified_view', this.videoProps(player.index));
        }
      }

      PROGRESS_MARKS.forEach((mark) => {
        if (percent < mark || stats.marks.has(mark)) return;
        stats.marks.add(mark);
        this.track('video_progress', { ...this.videoProps(player.index), percent: mark });
      });
    }

    commitVideoView(reason) {
      const view = this.videoView;
      if (!view) return;
      this.videoView = null;
      this.track('video_view_end', {
        ...this.videoProps(view.index),
        watch_ms: Math.round(view.watchMs),
        percent: Math.round(view.percent),
        reason,
      });
    }

    showHintOnce() {
      if (this.hintShown || sessionStorage.getItem('sf-hint') === '1') return;
      this.hintShown = true;
      sessionStorage.setItem('sf-hint', '1');

      const hint = document.createElement('div');
      hint.className = 'sf-hint';
      hint.innerHTML = `${ICONS.swipe}<span>Desliza</span>`;
      this.overlay.querySelector('.sf-frame').appendChild(hint);
      setTimeout(() => hint.remove(), 3600);
    }

    handleKey(event) {
      const step = (direction) => {
        event.preventDefault();
        const target = Math.max(0, this.activeIndex + direction);
        this.slides?.[target]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      };

      if (event.key === 'Tab') this.trapFocus(event);
      if (event.key === 'Escape') this.close();
      if (event.key === 'ArrowDown') step(1);
      if (event.key === 'ArrowUp') step(-1);
      if (event.key === 'ArrowRight') this.stepImage(this.activeIndex, 1);
      if (event.key === 'ArrowLeft') this.stepImage(this.activeIndex, -1);
    }

    trapFocus(event) {
      const focusable = Array.from(
        this.overlay.querySelectorAll(
          `.sf-topbar button:not([hidden]), .sf-slide[data-index="${this.activeIndex}"] :is(button:not([disabled]), a[href])`
        )
      );
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const inside = focusable.includes(document.activeElement);
      if (event.shiftKey && (!inside || document.activeElement === first)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (!inside || document.activeElement === last)) {
        event.preventDefault();
        first.focus();
      }
    }

    handleVisibility() {
      if (document.hidden) {
        clearInterval(this.heartbeatTimer);
        this.playerAt(this.activeIndex)?.el.pause();
        this.commitVideoView('hidden');
      } else if (this.overlay) {
        this.startHeartbeat();
        this.syncPlayers();
      }
    }

    async addToCart(index, button) {
      const item = this.productAt(index);
      const video = this.itemAt(index).sv;
      const variant = item.v[this.stateAt(index).variant];
      if (!variant || !variant.av || button.getAttribute('aria-busy') === 'true') return;

      button.setAttribute('aria-busy', 'true');
      const busySince = Date.now();
      const properties = video ? { _origen: `video-${this.surface}`, _video: String(video.id) } : { _origen: 'swipe-feed' };

      try {
        const sections = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
        );

        const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({
            items: [{ id: variant.id, quantity: 1, properties }],
            sections: sections.join(','),
            sections_url: window.location.pathname,
          }),
        });

        if (!response.ok) throw new Error('add failed');
        const added = await response.json();

        const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        cart.sections = added.sections;
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'swipe-feed:add', cart } })
        );

        await this.holdSpinner(busySince);
        this.stats.adds += 1;
        this.rememberAffinity(item);
        this.setCartCount(cart.item_count);
        button.removeAttribute('aria-busy');
        this.flashAdded(button);
        this.track('swipe_feed_add_to_cart', {
          product_id: item.id,
          handle: item.h,
          variant_id: variant.id,
          price: variant.p,
          position: index,
          images_seen: this.stateAt(index).image + 1,
        });
        if (video) this.track('video_add_to_cart', { ...this.videoProps(index), variant_id: variant.id, price: variant.p });
      } catch (error) {
        await this.holdSpinner(busySince);
        button.textContent = this.labels.error;
        setTimeout(() => this.renderPrice(this.slides[index], index), 1500);
      } finally {
        button.removeAttribute('aria-busy');
      }
    }

    rememberAffinity(item) {
      [item.ty, item.ve].forEach((key) => {
        if (key) this.affinity[key] = (this.affinity[key] || 0) + 1;
      });
      writeStorage(AFFINITY_KEY, this.affinity);
    }

    holdSpinner(startedAt) {
      const remaining = SPINNER_MIN_MS - (Date.now() - startedAt);
      return remaining > 0 ? new Promise((resolve) => setTimeout(resolve, remaining)) : Promise.resolve();
    }

    flashAdded(button) {
      button.classList.add('is-done');
      button.textContent = this.labels.added;
      this.cartButton.classList.add('is-bumped');
      setTimeout(() => this.cartButton.classList.remove('is-bumped'), 500);
      setTimeout(() => {
        button.classList.remove('is-done');
        button.textContent = this.labels.add;
      }, 1400);
    }

    async syncCartCount() {
      try {
        const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        this.setCartCount(cart.item_count);
      } catch (error) {
        this.setCartCount(0);
      }
    }

    setCartCount(count) {
      if (!this.countEl) return;
      this.countEl.textContent = String(count);
      this.countEl.hidden = count < 1;
    }

    openCartDrawer() {
      const drawer = document.querySelector('cart-drawer');
      this.close();
      if (drawer?.show) {
        setTimeout(() => drawer.show(), 220);
      } else {
        window.location.href = this.dataset.cartUrl;
      }
    }

    commitView() {
      if (this.activeIndex < 0 || !this.viewStartedAt) return;

      const dwell = Date.now() - this.viewStartedAt;
      this.viewStartedAt = null;

      const item = this.itemAt(this.activeIndex);
      if (!item) return;
      const product = this.productAt(this.activeIndex);

      this.seenIds.add(keyOf(item));
      this.stats.viewed += 1;
      this.track('swipe_feed_product_viewed', {
        product_id: product.id,
        handle: product.h,
        title: product.t,
        price: product.v[0]?.p,
        position: this.activeIndex,
        images_seen: this.stateAt(this.activeIndex).image + 1,
        dwell_ms: dwell,
        engaged: dwell >= VIEW_THRESHOLD_MS,
      });
    }

    startHeartbeat() {
      if (!this.analyticsEnabled) return;
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = setInterval(() => {
        if (!this.overlay || document.hidden) return;
        this.track('swipe_feed_heartbeat', {
          swipes_so_far: this.stats.swipes,
          adds_so_far: this.stats.adds,
        });
      }, this.heartbeatMs);
    }

    // Consent, PostHog and super properties are handled by window.lpTrack (assets/lp-track.js).
    track(name, properties = {}) {
      if (!this.analyticsEnabled) return;

      const payload = {
        feed_session_id: this.sessionId,
        section_id: this.dataset.sectionId,
        mode: this.mode,
        surface: this.surface,
        ...properties,
      };

      try {
        window.lpTrack?.(name, payload);
      } catch (error) {
        /* analytics must never break the feed */
      }

      window.dispatchEvent(new CustomEvent(`swipe-feed:${name}`, { detail: payload }));
      if (!window.Shopify?.customerPrivacy?.userCanBeTracked?.()) return;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: name, ...payload });
    }
  }

  if (!customElements.get('swipe-feed')) customElements.define('swipe-feed', SwipeFeed);
})();
